import React, {useRef, useState} from "react";
import PropTypes from "prop-types";

//components:
import {makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import {CustomSwitch, CustomTooltip, PaginationTable} from "../../../../widgets";

//styles:
const tracksListStyles = makeStyles(() => ({
    root: {
        width: "100%",
    },
    table: {
        minWidth: 500,
    },
}));

const CarouselsTable = ({t, carousels, editCarousel}) => {
    const classes = tracksListStyles();

    //local state:
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showEditTitleInput, setShowEditTitleInput] = useState("");
    const [showEditOrder, setShowEditOrder] = useState("");
    const [showMaxVideosEdit, setShowMaxVideosEdit] = useState("");

    //refs:
    const tableRef = useRef(null);

    const headRows = [
        {
            id: "order",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.ORDER"),
        },
        {
            id: "name",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.NAME"),
        },
        {
            id: "type_id",
            align: "center",
            disablePadding: false,
            label: t("TABLE.HEADER.TYPE"),
        },
        {
            id: "maxVideos",
            align: "center",
            disablePadding: false,
            label: t("ADMINPANEL.HOMEPAGE.CAROUSELS.TABLE.HEADER.MAX_VIDEOS"),
        },
        {
            id: "visibility",
            align: "center",
            disablePadding: false,
            label: t("TABLE.HEADER.ACTIONS"),
        },
    ];

    const onWheel = e => {
        e.preventDefault();
        if (window.innerWidth < 1750) {
            const container = tableRef.current;
            const containerScrollPosition = tableRef.current.scrollLeft;

            container.scrollTo({
                top: 0,
                left: containerScrollPosition + e.deltaY,
            });
        }
    };

    const desc = (a, b, orderBy) => {
        const itemA = a[orderBy] || 0;
        const itemB = b[orderBy] || 0;
        if (itemB < itemA) {
            return -1;
        }
        if (itemB > itemA) {
            return 1;
        }
        return 0;
    };

    const stableSort = (array, cmp) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map(el => el[0]);
    };

    const getSorting = (order, orderBy) => {
        return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
    };

    return (
        <div className={`${classes.root}`}>
            <div className={`custom-scroll-area horizontal-scroll`} onWheel={onWheel} ref={tableRef}>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            {headRows.map(row => (
                                <TableCell
                                    className="text-muted font-size-1rem"
                                    key={row.id}
                                    align={row.align}
                                    padding={row.disablePadding ? "none" : "default"}
                                >
                                    {row.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {carousels &&
                            stableSort(carousels, getSorting("asc", "order"))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map(carousel => {
                                    const isNormalTrack = carousel?.context?.track_id;
                                    return (
                                        <TableRow key={carousel.id}>
                                            {/* Column1: ORDER: */}
                                            <TableCell align="left" className="text-muted mb-0 font-size-1rem">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    {showEditOrder !== carousel.id ? (
                                                        <div className="min-w-20px">
                                                            {carousel.order || "0"}
                                                            <CustomTooltip
                                                                config={{
                                                                    text: t("TOOLTIP.EDIT_ORDER"),
                                                                    placement: "top",
                                                                }}
                                                            >
                                                                <button
                                                                    onClick={() => {
                                                                        setShowEditOrder(carousel.id);
                                                                    }}
                                                                    className="btn btn-icon btn-light ml-2 edit_button"
                                                                >
                                                                    <EditIcon />
                                                                </button>
                                                            </CustomTooltip>
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            autoFocus
                                                            className={`form-control form-control-solid px-2 text-dark-75 font-size-base w-60px`}
                                                            defaultValue={carousel.order || 0}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") {
                                                                    if (
                                                                        e.target.value &&
                                                                        Number(e.target.value) !== carousel.order
                                                                    )
                                                                        editCarousel(
                                                                            Number(e.target.value),
                                                                            "order",
                                                                            carousel.id
                                                                        );
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                if (
                                                                    e.target.value &&
                                                                    Number(e.target.value) !== carousel.order
                                                                )
                                                                    editCarousel(
                                                                        Number(e.target.value),
                                                                        "order",
                                                                        carousel.id
                                                                    );
                                                                else setShowEditOrder("");
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/* Column2: NAME */}
                                            <TableCell align="left" component="th" scope="row">
                                                <div className="d-flex flex-wrap w-100">
                                                    {showEditTitleInput !== carousel.id && (
                                                        <div
                                                            className={`${
                                                                showEditTitleInput === carousel.id ? "d-none" : ""
                                                            } text-dark-75 font-size-base mb-0 overflow-hidden py-2 px-4 elipsis_2lines`}
                                                            style={{
                                                                height: 42,
                                                            }}
                                                        >
                                                            {carousel.context && carousel.context.name
                                                                ? carousel.context.name
                                                                : carousel.name}
                                                            {!carousel.context || !carousel.context.track_id ? (
                                                                <CustomTooltip
                                                                    config={{
                                                                        text: t("TOOLTIP.EDIT_NAME"),
                                                                        placement: "top",
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={() => {
                                                                            setShowEditTitleInput(carousel.id);
                                                                        }}
                                                                        className="btn btn-icon btn-light ml-2 bottom-0 edit_button"
                                                                    >
                                                                        <EditIcon />
                                                                    </button>
                                                                </CustomTooltip>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                    {showEditTitleInput === carousel.id && (
                                                        <input
                                                            type="text"
                                                            autoFocus
                                                            className={`form-control form-control-solid h-auto py-2 px-4 text-dark-75 font-size-base w-100`}
                                                            defaultValue={carousel?.name}
                                                            onKeyDown={e => {
                                                                const value = e.target.value.replace(/\s\s+/g, " ");
                                                                if (e.key === "Enter") {
                                                                    const trimmedValue = value.trim();
                                                                    if (trimmedValue) {
                                                                        trimmedValue !== carousel?.name &&
                                                                            editCarousel(
                                                                                trimmedValue,
                                                                                "name",
                                                                                carousel.id
                                                                            );
                                                                    }
                                                                    e.target.value = trimmedValue;
                                                                    setShowEditTitleInput("");
                                                                } else {
                                                                    e.target.value = value;
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                const value = e.target.value.trim();
                                                                if (value) {
                                                                    value !== carousel?.name &&
                                                                        editCarousel(value, "name", carousel.id);
                                                                }
                                                                e.target.value = value;
                                                                setShowEditTitleInput("");
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/* Column3: TYPE: */}
                                            <TableCell align="center" component="th" scope="row">
                                                <div className={`trackType_badge ${isNormalTrack ? "" : "special"}`}>
                                                    {isNormalTrack ? "Track" : "Special"}
                                                </div>
                                            </TableCell>

                                            {/* Column4: MAX VIDEOS: */}
                                            <TableCell align="center" className="m-auto text-dark-75 font-size-1rem">
                                                <div className="d-flex flex-wrap w-100 justify-content-center align-items-center">
                                                    {showMaxVideosEdit !== carousel.id ? (
                                                        <>
                                                            {carousel.parameters?.find(
                                                                param => param.key === "maxVideos"
                                                            )?.value || "-"}
                                                            <CustomTooltip
                                                                config={{
                                                                    text: t(
                                                                        "ADMINPANEL.HOMEPAGE.CAROUSELS.TABLE.TOOLTIP.MAX_VIDEOS"
                                                                    ),
                                                                    placement: "top",
                                                                }}
                                                            >
                                                                <button
                                                                    className="btn btn-icon btn-light ml-2 edit_button"
                                                                    onClick={() => {
                                                                        setShowMaxVideosEdit(carousel.id);
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </button>
                                                            </CustomTooltip>
                                                        </>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            autoFocus
                                                            className={`form-control form-control-solid px-2 text-dark-75 font-size-base w-60px`}
                                                            defaultValue={
                                                                carousel.parameters?.find(
                                                                    param => param.key === "maxVideos"
                                                                )?.value || 0
                                                            }
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") {
                                                                    const defaultValue =
                                                                        carousel.parameters?.find(
                                                                            param => param.key === "maxVideos"
                                                                        )?.value || "-";

                                                                    if (
                                                                        e.target.value &&
                                                                        e.target.value !== defaultValue
                                                                    )
                                                                        editCarousel(
                                                                            Number(e.target.value) || 1,
                                                                            "maxVideos",
                                                                            carousel?.id
                                                                        );
                                                                    else setShowMaxVideosEdit("");
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                const defaultValue =
                                                                    carousel.parameters?.find(
                                                                        param => param.key === "maxVideos"
                                                                    )?.value || "-";

                                                                if (e.target.value && e.target.value !== defaultValue) {
                                                                    editCarousel(
                                                                        Number(e.target.value) || 1,
                                                                        "maxVideos",
                                                                        carousel?.id
                                                                    );
                                                                } else setShowMaxVideosEdit("");
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/*Column7: ACTIONS: */}
                                            <TableCell align="center" className="m-auto">
                                                {/* switcher: */}
                                                <CustomTooltip
                                                    config={{
                                                        text: t("TOOLTIP.SHOW_HIDE"),
                                                        placement: "top",
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-center">
                                                        <CustomSwitch
                                                            size="small"
                                                            checked={parseInt(carousel.visibility) || 0}
                                                            onChange={e =>
                                                                editCarousel(
                                                                    e.target.checked ? 1 : 0,
                                                                    "visibility",
                                                                    carousel.id
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </CustomTooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                        <TableRow style={{height: 48}}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    </TableBody>

                    {/* pagination: */}
                    <TableFooter>
                        <TableRow>
                            <PaginationTable
                                setPage={setPage}
                                setRowsPerPage={setRowsPerPage}
                                rows={carousels}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                rowsPerPageOptions={[5, 10, 25]}
                                variant={"filled"}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>
        </div>
    );
};

CarouselsTable.propTypes = {
    carousels: PropTypes.arrayOf(PropTypes.object).isRequired,
    editCarousel: PropTypes.func,
};

export default CarouselsTable;
